/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { ButtonOverrides } from "baseui/button";
import { ThemeType } from "./Theme/Theme";
import { StyleObject } from "styletron-react";

// Properties like icons, fonts, font sizes are taken care of in the
// button properties, but this function is used to derive a common color scheme.
export function getMenuButtonColorStyles(
  theme: ThemeType
): (args: any) => StyleObject {
  return ({ $isHovered, $isActive, $isSelected, $disabled }: any) => {
    const baseColor = theme.colors.buttonPrimaryColor;
    const hoverColor = theme.colors.buttonPrimaryHoverColor;
    const activeColor = theme.colors.buttonPrimaryHoverColor;
    const selectedColor = theme.colors.buttonPrimaryHoverColor;
    const textColor = theme.colors.primaryElementFontColor;

    let backgroundColor = baseColor;
    if ($isSelected) {
      backgroundColor = selectedColor;
    } else if ($isActive) {
      backgroundColor = activeColor;
    } else if ($isHovered) {
      backgroundColor = hoverColor;
    }

    return {
      backgroundColor,
      color: textColor,
      cursor: $disabled ? "not-allowed" : "pointer",
      opacity: $disabled ? 0.5 : 1,
      borderColor: "transparent",
      transition: "background-color 0.2s ease",
      boxShadow: "none",

      ":hover": {
        backgroundColor: hoverColor,
        color: textColor,
      },
      ":active": {
        backgroundColor: activeColor,
        color: textColor,
      },
      ":focus": {
        backgroundColor: hoverColor,
        color: textColor,
      },
      ...($isSelected && {
        backgroundColor: selectedColor,
        color: textColor,
      }),
    };
  };
}

export function getToolbarButtonColorStyles(
  theme: ThemeType
): (args: any) => StyleObject {
  return ({ $disabled, $isFocusVisible }: any) => {
    const hoverColor = theme.colors.primaryLight;
    const activeColor = theme.colors.primaryActive;
    const textColor = theme.colors.text;

    return {
      backgroundColor: "transparent",
      color: textColor,
      cursor: $disabled ? "not-allowed" : "pointer",
      opacity: $disabled ? 0.5 : 1,
      borderColor: "transparent",
      transition: "background-color 0.2s ease, color 0.2s ease",
      boxShadow: "none",

      ":hover": {
        backgroundColor: hoverColor,
        color: textColor,
      },
      ":active": {
        backgroundColor: activeColor,
        color: textColor,
      },
      ...($isFocusVisible
        ? {
            outline: `2px solid ${theme.colors.primary}`,
            outlineOffset: "2px",
          }
        : {
            // ensure mouse focus doesn't change bg
            ":focus": {
              backgroundColor: "transparent",
              color: textColor,
              outline: "none",
            },
          }),
    };
  };
}

export function getControlBarButtonStyles(
  theme: ThemeType
): (args: any) => StyleObject {
  const hoverColor = theme.colors.primaryLight;
  const textColor = theme.colors.text;

  return ({ $disabled }: any) => ({
    backgroundColor: "transparent",
    color: theme.colors.text,
    border: "none",
    borderRadius: "8px",
    boxShadow: "none",
    outline: "none",
    padding: "6px 0",
    margin: "0",
    minWidth: "unset",
    minHeight: "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.5 : 1,
    transition: "background-color 0.2s, color 0.2s",
    ":hover": {
      backgroundColor: hoverColor,
      color: textColor,
    },
  });
}

// Tertiary buttons used on the initialization modal.
export function getTertiaryButtonOverrides(theme: ThemeType): ButtonOverrides {
  return {
    BaseButton: {
      style: ({ $isHovered, $disabled }) => ({
        fontFamily: theme.fonts.primary,
        fontSize: theme.fontSizes.medium,
        backgroundColor: "transparent",
        color: $disabled ? theme.colors.secondaryTextColor : theme.colors.text,
        cursor: $disabled ? "not-allowed" : "pointer",
        opacity: $disabled ? 0.5 : 1,
        borderColor: "transparent",
        transition: "background-color 0.2s ease",
        boxShadow: "none",
        ...($isHovered && {
          backgroundColor: theme.colors.dropdownItemHoverBackground,
          color: theme.colors.text,
        }),
        ":hover": {
          backgroundColor: theme.colors.dropdownItemHoverBackground,
          color: theme.colors.text,
        },
        ":focus": {
          backgroundColor: theme.colors.dropdownItemHoverBackground,
          color: theme.colors.text,
        },
      }),
    },
  };
}

export function getSecondaryButtonOverrides(
  theme: ThemeType,
  extraStyle: StyleObject = {}
): ButtonOverrides {
  return {
    BaseButton: {
      style: ({ $disabled }) => ({
        ...extraStyle,
        fontFamily: theme.fonts.primary,
        fontSize: theme.fontSizes.medium,
        backgroundColor: theme.colors.buttonSecondaryColor,
        color: theme.colors.text,
        cursor: $disabled ? "not-allowed" : "pointer",
        opacity: $disabled ? 0.5 : 1,
        borderColor: "transparent",
        transition: "background-color 0.2s ease",
        boxShadow: "none",
        ":hover": {
          backgroundColor: theme.colors.buttonSecondaryHoverColor,
          color: theme.colors.text,
        },
      }),
    },
  };
}

export function getOutlinedButtonOverrides(theme: ThemeType): ButtonOverrides {
  return {
    BaseButton: {
      style: ({ $isHovered, $disabled }) => ({
        fontFamily: theme.fonts.primary,
        fontSize: theme.fontSizes.medium,
        backgroundColor: theme.colors.buttonPrimaryColor,
        color: theme.colors.primaryElementFontColor,
        cursor: $disabled ? "not-allowed" : "pointer",
        opacity: $disabled ? 0.5 : 1,
        borderColor: "transparent",
        transition: "background-color 0.2s ease",
        boxShadow: "none",
        ...($isHovered && {
          backgroundColor: theme.colors.dropdownItemHoverBackground,
          color: theme.colors.text,
        }),
        ":hover": {
          backgroundColor: theme.colors.dropdownItemHoverBackground,
          color: theme.colors.text,
        },
        ":focus": {
          backgroundColor: theme.colors.dropdownItemHoverBackground,
          color: theme.colors.text,
        },
      }),
    },
  };
}
